/**
 * RPC envelope + message type constants shared by the host (hostBridge.js,
 * ExtensionHost.js) and the guest realm (guestSdk.js, injected into the
 * sandboxed iframe). Kept dependency-free since guestSdk.js's source is
 * serialized as a string and executed outside the host's module graph.
 */

export const PROTOCOL_VERSION = '1.0.0';

// Host -> guest
export const HOST_TO_GUEST = {
  INIT: 'host:init',
  FREEZE: 'host:freeze',
  RESUME: 'host:resume',
  DISPOSE: 'host:dispose',
  RPC_RESULT: 'host:rpc-result',
};

// Guest -> host
export const GUEST_TO_HOST = {
  READY: 'guest:ready',
  RPC_CALL: 'guest:rpc-call',
  TREE_PUBLISH: 'guest:tree-publish',
  // Ask the host to open tree nodes (sdk.tree.expand). Additive: the host
  // owns expansion state; this only adds ids to it.
  TREE_EXPAND: 'guest:tree-expand',
  DISPOSED: 'guest:disposed',
  ERROR: 'guest:error',
};

// Bounded window (ms) the guest gets to flush state / close resources after
// a `dispose` message before the host tears the iframe down regardless.
export const DISPOSE_FLUSH_TIMEOUT_MS = 2000;

// Default idle-hidden grace period before a backgrounded extension is torn
// down (VS Code-style deactivation, not just paused).
export const DEFAULT_IDLE_TEARDOWN_MS = 60000;

// Per-RPC-call timeout; a hung guest can't accumulate unresolved promises.
export const RPC_CALL_TIMEOUT_MS = 15000;

export function makeEnvelope(type, payload, extra = {}) {
  return { type, payload, ts: Date.now(), ...extra };
}

/**
 * Per-mount shared secret between ONE ExtensionHost and the single guest
 * iframe it owns. Generated host-side, inlined into that iframe's srcdoc, and
 * required on every envelope in both directions.
 *
 * Why a secret at all when both realms also check `event.source`: the source
 * check authenticates "the frame on the other end of this parent/child edge",
 * which is exactly right today (ExtensionHostLayer mounts every guest as a
 * direct child of the host document, so `window.parent` IS the host and every
 * other extension is a sibling). It stops being right the moment a wrapper
 * frame is introduced between host and guest — a change nobody would think of
 * as security-relevant. The token survives that: it lives in the srcdoc
 * attribute of one iframe in the HOST document, and a sandboxed
 * opaque-origin guest has no same-origin access to the host realm, so no
 * other extension can read it.
 *
 * 128 bits from the CSPRNG. The Math.random fallback exists only so tests and
 * non-browser environments can construct a bridge; it is never reached in the
 * app (Tauri's webview always has WebCrypto).
 */
export function newFrameToken() {
  const bytes = new Uint8Array(16);
  const webcrypto = typeof window !== 'undefined' ? window.crypto : undefined;
  if (webcrypto && typeof webcrypto.getRandomValues === 'function') {
    webcrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

let callSeq = 0;
export function nextCallId() {
  callSeq += 1;
  return `rpc-${callSeq}-${Date.now()}`;
}
